import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAlerts } from "../../contexts/AlertsContext";
import { useApiClient } from "../../contexts/ApiClientContext";
import APIEndPoints from "../../utils/APIEndPoints";
import Button from "../common/button/Button";
import ButtonAction from "../common/button/ButtonAction";
import SETTINGS from "../../globals";
import AppSections from "../../utils/AppSections";
import AppSubSections from "../../utils/AppSubSections";
import ButtonInfo from "../common/button/ButtonInfo";
import AppIcons from "../../utils/AppIcons";
import Loading from "../common/Loading";
import DialogWindowType from "../common/dialog_window/DialogWindowType";
import AlertMessageType from "../common/alert_message/AlertMessageType";
import { IApiClientError } from "../../shared/ApiClient";
import ButtonType from "../common/button/ButtonType";
import ImgUtil from "../../utils/ImgUtil";
import { useAuth } from "../../contexts/AuthContext";
import Pilot from "../../model/Pilot";
import PagedData from "../../model/PagedData";
import Site from "../../model/Site";
import SitesGridElement from "../sites/SitesGridElement";
import FormDropDown from "../common/FormDropDown";
import EmbeddingToolInfo from "../../utils/EmbeddingToolInfo";
import ReportButton from "../common/ReportButton";

function PilotDetails() {
  // read the ID from the url
  const { id } = useParams();

  // reference to the AuthManager
  const authManager = useAuth();
  // manage loading animation
  const [loading, setLoading] = useState(true);
  // set the data
  const [data, setData] = useState<Pilot>(Pilot.EmptyInstance());
  // set the state to track any error messages received from the server
  const [errorMessage, setErrorMessage] = useState("");
  // manage the list of all potential sites
  const [availableSites, setAvailableSites] = useState<Site[]>([]);
  // manage selected site to be added
  const [selectedSiteId, setSelectedSiteId] = useState<number | undefined>();
  // reference to the ApiClient instance
  const apiClient = useApiClient();
  // get a reference to navigation component
  const navigate = useNavigate();
  // reference to the alerts system
  const alertsManager = useAlerts();

  const getAllData = async () => {
    try {
      let pilot: Pilot | undefined = undefined;
      if (id) {
        pilot = await apiClient?.get<Pilot>({
          url: APIEndPoints.PILOTS.ONE(parseInt(id)),
        });
        setData(pilot);
      }

      let allSitesInfo = await apiClient?.get<PagedData<Site[]>>({
        url: APIEndPoints.SITES.ALL,
      });
      const addedSitesIDs = pilot?.sites?.map((site) => site.id!);
      setAvailableSites(
        allSitesInfo.items.filter(
          (site) => addedSitesIDs?.indexOf(site.id!) === -1,
        ),
      );
    } catch (error: any) {
      if (!error.processed) {
        setErrorMessage("Something went wrong. Try again later!");
      }
    }
    setLoading(false);
  };

  // load record data from the server
  useEffect(() => {
    getAllData();
  }, [apiClient, id]);

  const handleDelete = () => {
    apiClient
      ?.delete({
        url: APIEndPoints.PILOTS.ONE(parseInt(id!)),
      })
      .then((response) => {
        alertsManager.showAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: "CH Element record deleted successfully",
        });
        navigate(SETTINGS.get_path({ section: AppSections.PILOTS }));
      })
      .catch((error: IApiClientError) => {
        if (!error.processed) {
          alertsManager.showAlertMessage({
            type: AlertMessageType.ERROR,
            message: "Unable to delete the CH Element record",
          });
        }
      });
  };

  const addSiteToPilot = () => {
    // check if we have a site selected
    if (!selectedSiteId) {
      return;
    }

    const idSites = [];
    data.sites?.map((site) => idSites.push(site.id));
    idSites.push(selectedSiteId);

    updatePilot(idSites);
  };

  const removeSiteFromPilot = (id: number) => {
    const idSites: number[] = [];
    data.sites
      ?.filter((site) => site.id !== id)
      .map((site) => idSites.push(site.id!));
    updatePilot(idSites);
  };

  const updatePilot = (idSites: number[]) => {
    var body = new Pilot(
      authManager.getUserID()!!,
      data.name,
      data.description,
      data.icon,
      idSites,
    );

    apiClient
      ?.put({
        url: APIEndPoints.PILOTS.ONE(data.id!),
        body: body,
      })
      .then((response) => {
        alertsManager.showAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: "Pilot record updated successfully",
        });
        getAllData();
      })
      .catch((error: IApiClientError) => {
        console.log(error);
      });
  };

  const siteSelectionChanged = (id: string, value: string) => {
    setSelectedSiteId(parseInt(value));
  };

  if (loading) {
    return <Loading />;
  }

  const canEdit =
    !EmbeddingToolInfo.isChemiTool() &&
    (data.id_user === authManager.getUserID() ||
      authManager.isAdmin());

  const sitesOptions = availableSites.map((site) => {
    return { value: site.id + "", label: site.name };
  });
  sitesOptions.splice(0, 0, {
    value: "-1",
    label: "-- please select site --",
  });

  return (
    <div className="details-page">
      <div className="details-header">
        {/* Image */}
        <div className="details-icon">
          <div>
            <img
              src={
                ImgUtil.display_icon(data.icon_path) ||
                process.env.REACT_APP_PUBLIC_URL +
                  "/resources/images/no-image.png"
              }
              alt="Pilot icon"
            />
          </div>
        </div>

        {/* Info Section */}
        <div className="details-header-content">
          <div className="details-title">
            <h1>{data.name}</h1>
          </div>
          <div className="details-overview">
            <ButtonInfo>
              {AppIcons.SITE} sites enroled: <span>{data.sites?.length}</span>
            </ButtonInfo>
            <div></div>
            <div></div>
            <ButtonInfo className="span-3">
              {AppIcons.GUID} GUID: <span>{data.GUID}</span>
            </ButtonInfo>
          </div>

        <div className="flex items-start mt-5 gap-4"> 
          {canEdit && (

              <Button
                label="Delete Pilot"
                purpose={ButtonAction.DELETE}
                onClick={() => {
                  alertsManager.showDialogWindow({
                    type: DialogWindowType.CONFIRM,
                    message: "Are you sure you want to delete this Pilot?",
                    cancelLabel: "Cancel",
                    confirmLabel: "Yes, delete Pilot",
                    onConfirm: handleDelete,
                  });
                }}
              />
          
          )}

          <ReportButton
                entityType="Case Study"
                entityName={data.name}
                entityId={data.id || 0}
          />
          </div>
        </div>
      </div>

      {/* Details Section */}
      <div className="details-section">
        <div className="details-section-title">
          <h2>Pilot details</h2>
          {canEdit && (
            <Button
              label="Edit information"
              purpose={ButtonAction.EDIT}
              type={ButtonType.ICON_TEXT}
              onClick={() =>
                navigate(
                  SETTINGS.get_path({
                    section: AppSections.PILOTS,
                    id: data.id,
                    subsection: AppSubSections.EDIT,
                  }),
                )
              }
            />
          )}
        </div>
        <p className="details-description">{data.description}</p>
      </div>

      {/* Add site section */}
      {authManager.isAdmin() && (
        <div className="details-section">
          <div className="details-section-title">
            <h2>Add new sites to this pilot</h2>
          </div>
          <div className="details-section-content">
            {availableSites.length > 0 && (
              <form onSubmit={addSiteToPilot} className="flex flex-row">
                <div className="grow">
                  <FormDropDown
                    label="Select site"
                    id="available_site"
                    options={sitesOptions}
                    onChange={siteSelectionChanged}
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    label="Add site"
                    purpose={ButtonAction.CREATE}
                    onClick={addSiteToPilot}
                  />
                </div>
              </form>
            )}
            {availableSites.length === 0 && (
              <div className="details-no-records">
                There are no sites available to be included in this pilot.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Included sites section */}
      <div className="details-section">
        <div className="details-section-title">
          <h2>Sites included in this pilot</h2>
        </div>
        <div className="details-section-content">
          {data.sites && data.sites.length > 0 && (
            <div className="details-grid">
              {data.sites?.map((site) => {
                return (
                  <SitesGridElement
                    key={"elem_key_" + site.id!}
                    data={site}
                    canEdit={authManager.isAdmin()}
                    onRemove={removeSiteFromPilot}
                  />
                );
              })}
            </div>
          )}
          {!data.sites ||
            (data.sites.length === 0 && (
              <div className="details-no-records">
                There are no sites included in this pilot.
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

export default PilotDetails;
